import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { lighthouseService } from '@/api/services'
import type { SystemFeesPayload } from '@ankor-io/common/types/pay/fee'
import type { FeatureToggleName, FeatureToggles, UpdateFeatureTogglesBody } from '@ankor-io/common/types/system'

export const systemKeys = {
  globalToggles: () => ['global', 'toggles'] as const,
  globalFees: () => ['global', 'fees'] as const,
}
/**
 * TanStack Query hook for fetching global feature toggles.
 *
 * @returns Query result with global feature toggles data, loading state, and error
 */
export function useGlobalTogglesQuery(options?: { enabled?: boolean }) {
  return useQuery<FeatureToggles, Error>({
    queryKey: systemKeys.globalToggles(),
    queryFn: () => lighthouseService.getGlobalToggles(),
    enabled: options?.enabled !== false,
  })
}

/**
 * TanStack Mutation hook for updating global feature toggles.
 * Uses optimistic updates for instant UI feedback, with automatic rollback on error.
 * Automatically invalidates the global toggles query on success to ensure consistency.
 *
 * @returns Mutation result with mutate function, loading state, and error
 */
export function useUpdateGlobalTogglesMutation() {
  const queryClient = useQueryClient()
  return useMutation<
    FeatureToggles,
    Error,
    UpdateFeatureTogglesBody,
    { previousData: FeatureToggles | undefined } | undefined
  >({
    mutationFn: (body: UpdateFeatureTogglesBody) => lighthouseService.updateGlobalToggles(body),
    onMutate: async (variables) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: systemKeys.globalToggles() })

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<FeatureToggles>(systemKeys.globalToggles())

      // Optimistically update the cache
      if (previousData) {
        const optimisticData: FeatureToggles = { ...previousData }

        // Update each toggle in the mutation
        for (const toggle of variables.toggles) {
          const toggleName = toggle.name as FeatureToggleName
          optimisticData[toggleName] = toggle.value
        }

        queryClient.setQueryData(systemKeys.globalToggles(), optimisticData)
      }

      // Return context with previous data for rollback
      return { previousData }
    },
    onError: (_error, _variables, context) => {
      // Rollback to previous data on error
      if (context?.previousData) {
        queryClient.setQueryData(systemKeys.globalToggles(), context.previousData)
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure consistency
      queryClient.invalidateQueries({ queryKey: systemKeys.globalToggles() })
    },
  })
}

export function useDeleteGlobalToggleMutation(name: FeatureToggleName) {
  const queryClient = useQueryClient()
  return useMutation<void, Error, void, { previousData: FeatureToggles | undefined } | undefined>({
    mutationFn: () => lighthouseService.deleteGlobalToggle(name),
    onMutate: async () => {
      if (!name) return undefined

      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: systemKeys.globalToggles() })

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<FeatureToggles>(systemKeys.globalToggles())

      // Optimistically update the cache
      if (previousData) {
        const optimisticData = Object.fromEntries(
          Object.entries(previousData).filter(([key]) => key !== name)
        ) as FeatureToggles
        queryClient.setQueryData(systemKeys.globalToggles(), optimisticData)
      }

      // Return context with previous data for rollback
      return { previousData }
    },
    onError: (_error, _toggleName, context) => {
      // Rollback to previous data on error
      if (context?.previousData) {
        queryClient.setQueryData(systemKeys.globalToggles(), context.previousData)
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure consistency
      queryClient.invalidateQueries({ queryKey: systemKeys.globalToggles() })
    },
  })
}
export function useGlobalFeesQuery(options?: { enabled?: boolean }) {
  return useQuery<SystemFeesPayload, Error>({
    queryKey: systemKeys.globalFees(),
    queryFn: () => lighthouseService.getSystemFees(),
    enabled: options?.enabled !== false,
  })
}
